/**
 * HeadlineReviewDialog Component
 *
 * Displays generated headline options for user selection or revision request.
 * Automatically opens when AI post reaches "awaiting_headline_approval" phase.
 *
 * Story: BLOG-AI-003, AC-13, AC-14, AC-15, Task 11a
 */
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Loader2, Check } from 'lucide-react'
import { toast } from 'sonner'
import { apiService } from '@/services/api'
import { cn } from '@/lib/utils'

interface BlogPost {
  id: string
  title: string
  ai_metadata?: {
    phase: string
    headline_options?: string[]
    headline_revision_count?: number
  }
}

interface HeadlineReviewDialogProps {
  post: BlogPost | null
  open: boolean
  onClose: () => void
}

export function HeadlineReviewDialog({ post, open, onClose }: HeadlineReviewDialogProps) {
  const queryClient = useQueryClient()
  const [selectedHeadline, setSelectedHeadline] = useState<string | null>(null)
  const [revisionFeedback, setRevisionFeedback] = useState('')
  const [showFeedback, setShowFeedback] = useState(false)

  const headlines = post?.ai_metadata?.headline_options || []
  const revisionCount = post?.ai_metadata?.headline_revision_count || 0

  // Approve headline mutation
  const approveMutation = useMutation({
    mutationFn: async () => {
      if (!post || !selectedHeadline) throw new Error('No headline selected')
      return apiService.blog.aiWriter.approveHeadline(post.id, {
        selected_headline: selectedHeadline,
      })
    },
    onSuccess: () => {
      toast.success('Headline selected! Creating outline now...')
      queryClient.invalidateQueries({ queryKey: ['blog-posts'] })
      onClose()
      setSelectedHeadline(null)
    },
    onError: (error: any) => {
      console.error('Failed to approve headline:', error)
      toast.error(error?.response?.data?.detail || 'Failed to select headline')
    },
  })

  // Revise headlines mutation
  const reviseMutation = useMutation({
    mutationFn: async () => {
      if (!post || !revisionFeedback.trim()) throw new Error('No feedback provided')
      return apiService.blog.aiWriter.reviseHeadlines(post.id, {
        feedback: revisionFeedback,
      })
    },
    onSuccess: () => {
      toast.success('Generating new headlines with your feedback...')
      queryClient.invalidateQueries({ queryKey: ['blog-posts'] })
      onClose()
      setRevisionFeedback('')
      setShowFeedback(false)
    },
    onError: (error: any) => {
      console.error('Failed to request new headlines:', error)
      toast.error(error?.response?.data?.detail || 'Failed to request new headlines')
    },
  })

  const handleApprove = () => {
    if (!selectedHeadline) {
      toast.error('Please select a headline')
      return
    }
    approveMutation.mutate()
  }

  const handleRevise = () => {
    if (!revisionFeedback.trim()) {
      toast.error('Please provide feedback for the revision')
      return
    }
    reviseMutation.mutate()
  }

  if (!post || !headlines.length) return null

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-card text-card-foreground border-border sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-foreground">Choose Your Headline</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Select the headline that best fits your blog post, or request different options.
            {revisionCount > 0 && (
              <span className="block mt-1 text-sm">
                (Revision #{revisionCount + 1})
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Headline Options */}
          <div className="space-y-3">
            <Label className="text-base font-semibold text-foreground">
              Headline Options
            </Label>
            <div className="space-y-2">
              {headlines.map((headline, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedHeadline(headline)}
                  className={cn(
                    'w-full text-left p-4 rounded-lg border-2 transition-all',
                    'hover:bg-accent hover:border-primary/50',
                    selectedHeadline === headline
                      ? 'border-primary bg-primary/5'
                      : 'border-border bg-background'
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={cn(
                        'mt-0.5 h-5 w-5 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                        selectedHeadline === headline
                          ? 'border-primary bg-primary'
                          : 'border-muted-foreground'
                      )}
                    >
                      {selectedHeadline === headline && (
                        <Check className="h-3 w-3 text-primary-foreground" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-foreground">{headline}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {headline.length} characters
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Revision Feedback Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold text-foreground">
                Want Different Headlines?
              </Label>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFeedback(!showFeedback)}
                className="text-primary hover:text-primary/80"
              >
                {showFeedback ? 'Hide' : 'Show'} Feedback
              </Button>
            </div>

            {showFeedback && (
              <div className="space-y-2">
                <Textarea
                  value={revisionFeedback}
                  onChange={(e) => setRevisionFeedback(e.target.value)}
                  placeholder="E.g., Make them more engaging, add numbers, focus on benefits..."
                  rows={3}
                  className="bg-background border-border text-foreground placeholder:text-muted-foreground focus:ring-ring"
                />
                <p className="text-xs text-muted-foreground">
                  Provide specific feedback to help the AI generate better headlines.
                </p>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex gap-2 sm:justify-between">
          <Button
            variant="outline"
            onClick={handleRevise}
            disabled={!revisionFeedback.trim() || reviseMutation.isPending}
            className="border-border hover:bg-accent"
          >
            {reviseMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Revising...
              </>
            ) : (
              'Request Different Headlines'
            )}
          </Button>

          <Button
            onClick={handleApprove}
            disabled={!selectedHeadline || approveMutation.isPending}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {approveMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Continuing...
              </>
            ) : (
              'Continue with This Headline'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
